// ActionPanel.tsx
import React, { memo, useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { Tooltip, TooltipContent, TooltipTrigger } from './ui/tooltip';
import { LucideIcon } from '@/components/ui/lucide-memo';

interface ActionButtonProps {
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  tooltipText: string;
  icon: LucideIcon | React.ComponentType<{ color?: string; size?: number }>;
  iconColor?: string;
  selected?: boolean;
  tooltipSide?: 'top' | 'left' | 'right' | 'bottom';
  children?: React.ReactNode;
}

const BUTTON_BASE_STYLE: React.CSSProperties = {
  borderRadius: '8px',
  cursor: 'pointer',
  pointerEvents: 'auto',
};

const MENU_STYLE: React.CSSProperties = { position: 'relative' };

const stopPropagation = (event: React.MouseEvent<HTMLDivElement>) => event.stopPropagation();

export const ActionButton: React.FC<ActionButtonProps> = memo(({
  onClick: originalOnClick,
  tooltipText,
  icon: Icon,
  iconColor: propIconColor,
  selected = false,
  tooltipSide = 'top',
  children
}) => {
  const defaultSelectedIconColor = '#0D0D0D';
  const iconColor = selected && !propIconColor ? defaultSelectedIconColor : propIconColor || 'white';
  // Studio's selected tool: a near-white circle with a dark glyph (ChatGPT's), not pale blue.
  const backgroundColor = selected ? '#ECECEC' : undefined;
  const buttonRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (isMenuOpen && menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false); // Close the menu
      }
    };

    if (isMenuOpen)  document.addEventListener('mousedown', handleOutsideClick);
    else          document.removeEventListener('mousedown', handleOutsideClick);
    return () => {document.removeEventListener('mousedown', handleOutsideClick);};
    
  }, [isMenuOpen]); // Effect depends on isMenuOpen

  const handleClick = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    if (event.button === 0 && originalOnClick) { // Left click
      originalOnClick(event);
    }
  }, [originalOnClick]);

  const hasChildren = Boolean(children);
  const handleContextMenu = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault(); // Prevent default context menu
    event.stopPropagation();

    setIsMenuOpen((open) => (hasChildren ? !open : false));
  }, [hasChildren]);

  const buttonStyle = useMemo(
    () => ({ ...BUTTON_BASE_STYLE, backgroundColor }),
    [backgroundColor],
  );

  return (
    <div>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            ref={buttonRef}
            onClick={handleClick}
            onContextMenu={handleContextMenu}
            className={`p-2 classic ${isMenuOpen && 'gray--selected'}`}
            style={buttonStyle}
          >
            <Icon color={iconColor} size={16} />
          </div>
        </TooltipTrigger>
        <TooltipContent side={tooltipSide} sideOffset={8}>{tooltipText}</TooltipContent>
      </Tooltip>
      {isMenuOpen && children && (
        <div ref={menuRef} onClick={stopPropagation} style={MENU_STYLE}>
          {children}
        </div>
      )}
    </div>
  );
});

ActionButton.displayName = 'ActionButton';
